

import { create, useStore } from "zustand";
import { LeadsWizardActiveStepEnum, TabsEnum } from '../api/models/Enums';
import { LeadEntity } from '../api/models/Leads/LeadEntity';
import { devtools } from "zustand/middleware";
import { OpportunityFunnelValues, ReactSelectOption } from "../types";
import { LeadWizardSteps } from '../api/models/Leads/LeadWizardSteps'
import {
	DealerDTO,
	OpportunityVehicleDTO,
	UserDTO,
} from '../api';
import { AccountEntitySearch } from '../api/models/AccountEntity';
import { useShallow } from 'zustand/react/shallow';

interface WizardFunnelData {
	account: AccountEntitySearch
	opportunity: OpportunityFunnelValues
	vehicle: OpportunityVehicleDTO | null
	sendEmailData?: SendEmailData | null
	motornetSelectedVehicle?: {
    brand: ReactSelectOption | null,
    model: ReactSelectOption | null,
    version?: ReactSelectOption | null,
    images: string[]
  },
}

interface SendEmailData {
	enabled?: boolean;
	message?: string;
	to?: ReactSelectOption | string | null;
	dealer?: ReactSelectOption | null;
	dealerDocs?: string[];
	cc?: ReactSelectOption[];
	files?: File[];
}

export interface LeadsProps<T> {
	activeTab: TabsEnum;
	activeLead: any;
	checkedLeads: string[];
	userSalesOptions: ReactSelectOption[];
	wizardActiveStep: LeadWizardSteps;
	opportunityValues: OpportunityFunnelValues;
	leadWizardData: WizardFunnelData;
	callCounter: number;
	vehicleSource: string;
	dealers: DealerDTO[];
	salesDealer?: DealerDTO | null;
	currentUserIsRefDealer?: boolean;
	salesUsers: UserDTO[];
	isConvertingLead?: boolean;

	setActiveTab: (value: TabsEnum) => void;
	setActiveLead: (value: any) => void;
	setWizardActiveStep: (value: LeadWizardSteps) => void;
	toggleCheckedLead: (lead: LeadEntity) => void;
	clearCheckedLeads: () => void;
	setCheckedLeads: (leads: string[]) => void;
	setUserSalesOptions: (options: ReactSelectOption[]) => void;
	setOpportunityValues: (values: OpportunityFunnelValues) => void;
	setLeadWizardData: (wizardFunnelData: Partial<WizardFunnelData>) => void;
	cleanLeadWizardData: () => void;
	cleanActiveLead: () => void;
	setCallCounter: (value: number) => void;
	setVehicleSource: (source: string) => void;
	setDealers: (dealers: DealerDTO[]) => void;
	setSalesDealer: (dealer: DealerDTO) => void;
	setSalesUsers: (salesUsers: UserDTO[]) => void;
	setCurrentUserIsRefDealer: (currentUserIsRefDealer: boolean) => void;
	setIsConvertingLead: (isConvertingLead: boolean) => void;
}

const defaultLeadWizardData: WizardFunnelData = {
	account: {} as AccountEntitySearch,
	opportunity: {} as OpportunityFunnelValues,
	vehicle: null,
	sendEmailData: null
};

const useLeadsStore = create<LeadsProps<any>>()(devtools((set) => ({
	activeTab: TabsEnum.NEW_LEADS,
	userSalesOptions: [],
	activeLead: null,
	checkedLeads: [],
	wizardActiveStep: 'account',
	leadWizardData: defaultLeadWizardData,
	callCounter: 0,
	vehicleSource: 'hmp',
	dealers: [],
	salesDealer: null,
	salesUsers: [],
	isConvertingLead: false,

	opportunityValues: {
		Tipologia__c: null,
		Canale__c: null,
		Noleggiatore__c: null,
		commissions: '',
		name: '',
		driveItEasy: false,
		driverEmail: '',
		notes: '',
		nomePartner: null,
		salesUser: "",
		referenceUser: "",
		backupUsers: [],
	},

	setActiveTab: (value) => set({ activeTab: value }),
	setActiveLead: (value) => set({ activeLead: value }),
	setDealers: (dealers) => set({ dealers }),
	setSalesDealer: (salesDealer) => set({ salesDealer }),
	setCurrentUserIsRefDealer: (currentUserIsRefDealer) =>
		set({ currentUserIsRefDealer }),
	setSalesUsers: (salesUsers) => set({ salesUsers }),
	toggleCheckedLead: (lead: LeadEntity) => {
		const leadId = lead.Id ?? "";
		if (!leadId) return;

		set((state) => {
			let updatedCheckedLeads: string[];
			if (Array.isArray(state.checkedLeads) && state.checkedLeads.includes(leadId)) {
				updatedCheckedLeads = state.checkedLeads.filter((id) => id !== leadId);
			} else {
				updatedCheckedLeads = [...(Array.isArray(state.checkedLeads) ? state.checkedLeads : []), leadId];
			}
			return { checkedLeads: updatedCheckedLeads };
		});
	},

	setUserSalesOptions: (options) => set({ userSalesOptions: options }),
	clearCheckedLeads: () => set({ checkedLeads: [] }),
	setWizardActiveStep: (value) => set({ wizardActiveStep: value }),
	setCheckedLeads: (leads) => set({ checkedLeads: Array.isArray(leads) ? leads : [] }),
	setOpportunityValues: (values) => set({ opportunityValues: values }),
	setCallCounter: (value) => set({ callCounter: value }),
	setLeadWizardData: (partialData) =>
		set((state) => ({
			leadWizardData: {
				...state.leadWizardData,
				...partialData,
			},
		})),
	cleanLeadWizardData: () => set(() => ({
		leadWizardData: defaultLeadWizardData,
		wizardActiveStep: LeadsWizardActiveStepEnum.ACCOUNT,
		vehicleSource: 'hmp',
		isConvertingLead: false,
	})),
	cleanActiveLead: () => set(() => ({
		activeLead: null,
		isConvertingLead: false,
	})),
	setVehicleSource: (source) => set({ vehicleSource: source }),
	setIsConvertingLead: (isConvertingLead) => set({ isConvertingLead }),
}),
	{ name: "LeadStore" }
));

export function useLeadSelectedVehicle() {
	return useStore(
		useLeadsStore,
		useShallow((state) => state.leadWizardData?.motornetSelectedVehicle),
	);
}

export default useLeadsStore;
